// ─── Feed Management Data ────────────────────────────────────────────────────

export type FeedCategory = 'Starter' | 'Grower' | 'Finisher' | 'Layer' | 'Breeder' | 'Supplement';
export type FeedUnit = 'kg' | 'bags' | 'tonnes';
export type StockStatus = 'In Stock' | 'Low Stock' | 'Out of Stock' | 'Ordered';
export type IssuanceStatus = 'Issued' | 'Pending' | 'Returned' | 'Partial';

export interface FeedType {
  id: string;
  code: string;
  name: string;
  category: FeedCategory;
  manufacturer: string;
  description: string;
  proteinContent: number; // %
  energyContent: number; // kcal/kg
  unitCost: number; // UGX per kg
  unit: FeedUnit;
  targetBirdType: string;
  ageRangeWeeks: string;
  isActive: boolean;
  createdAt: string;
}

export interface FeedStock {
  id: string;
  feedTypeId: string;
  feedTypeName: string;
  feedCode: string;
  category: FeedCategory;
  farm: string;
  store: string;
  quantityKg: number;
  reorderLevelKg: number;
  maxCapacityKg: number;
  lastReceivedDate: string;
  lastReceivedQty: number;
  unitCost: number;
  totalValue: number;
  status: StockStatus;
  batchNumber: string;
  expiryDate: string;
  supplier: string;
}

export interface FeedIssuance {
  id: string;
  issuanceNumber: string;
  date: string;
  feedTypeId: string;
  feedTypeName: string;
  feedCode: string;
  flockId: string;
  flockName: string;
  house: string;
  farm: string;
  quantityIssuedKg: number;
  quantityReturnedKg: number;
  quantityConsumedKg: number;
  wastageKg: number;
  issuedBy: string;
  receivedBy: string;
  status: IssuanceStatus;
  notes: string;
  birdCount: number;
  feedPerBirdGrams: number;
}

export interface FeedConsumption {
  id: string;
  date: string;
  flockId: string;
  flockName: string;
  house: string;
  farm: string;
  feedTypeName: string;
  feedCode: string;
  openingStockKg: number;
  issuedKg: number;
  returnedKg: number;
  closingStockKg: number;
  consumedKg: number;
  wastageKg: number;
  birdCount: number;
  avgConsumptionPerBirdGrams: number;
  weekNumber: number;
}

export interface FeedReturn {
  id: string;
  returnNumber: string;
  date: string;
  issuanceId: string;
  issuanceNumber: string;
  feedTypeName: string;
  flockName: string;
  house: string;
  farm: string;
  returnedQtyKg: number;
  reason: string;
  condition: 'Good' | 'Damaged' | 'Contaminated';
  returnedBy: string;
  receivedBy: string;
  notes: string;
}

export interface FCRRecord {
  id: string;
  flockId: string;
  flockName: string;
  house: string;
  farm: string;
  birdType: string;
  weekNumber: number;
  periodStart: string;
  periodEnd: string;
  totalFeedConsumedKg: number;
  totalWeightGainKg: number;
  fcr: number;
  targetFcr: number;
  fcrVariance: number;
  avgBodyWeightKg: number;
  feedCostPerKg: number;
  totalFeedCost: number;
  status: 'Excellent' | 'Good' | 'Average' | 'Poor';
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

export const MOCK_FEED_TYPES: FeedType[] = [
  {
    id: 'ft-001', code: 'BRS-001', name: 'Broiler Starter Crumbles', category: 'Starter',
    manufacturer: 'Ugachick Feeds Ltd', description: 'High-protein starter feed for broiler chicks 0–14 days',
    proteinContent: 22, energyContent: 3050, unitCost: 2800, unit: 'kg',
    targetBirdType: 'Broiler', ageRangeWeeks: '0–2', isActive: true, createdAt: '2026-01-10',
  },
  {
    id: 'ft-002', code: 'BRG-002', name: 'Broiler Grower Pellets', category: 'Grower',
    manufacturer: 'Ugachick Feeds Ltd', description: 'Balanced grower feed for broilers 15–28 days',
    proteinContent: 20, energyContent: 3100, unitCost: 2600, unit: 'kg',
    targetBirdType: 'Broiler', ageRangeWeeks: '2–4', isActive: true, createdAt: '2026-01-10',
  },
  {
    id: 'ft-003', code: 'BRF-003', name: 'Broiler Finisher Pellets', category: 'Finisher',
    manufacturer: 'Ugachick Feeds Ltd', description: 'Energy-dense finisher for market-weight broilers',
    proteinContent: 18, energyContent: 3200, unitCost: 2500, unit: 'kg',
    targetBirdType: 'Broiler', ageRangeWeeks: '4–6', isActive: true, createdAt: '2026-01-10',
  },
  {
    id: 'ft-004', code: 'LAY-001', name: 'Layer Mash Premium', category: 'Layer',
    manufacturer: 'Nile Agro Feeds', description: 'Complete layer mash with calcium for optimal shell quality',
    proteinContent: 17, energyContent: 2750, unitCost: 2400, unit: 'kg',
    targetBirdType: 'Layer', ageRangeWeeks: '18+', isActive: true, createdAt: '2026-01-15',
  },
  {
    id: 'ft-005', code: 'LAY-002', name: 'Layer Pellets Hi-Cal', category: 'Layer',
    manufacturer: 'Nile Agro Feeds', description: 'High-calcium layer pellets for peak production',
    proteinContent: 16, energyContent: 2800, unitCost: 2550, unit: 'kg',
    targetBirdType: 'Layer', ageRangeWeeks: '20+', isActive: true, createdAt: '2026-02-01',
  },
  {
    id: 'ft-006', code: 'SUP-001', name: 'Vitamin & Mineral Premix', category: 'Supplement',
    manufacturer: 'Agri-Tech Solutions', description: 'Concentrated vitamin-mineral supplement for all poultry',
    proteinContent: 0, energyContent: 0, unitCost: 18500, unit: 'kg',
    targetBirdType: 'All', ageRangeWeeks: 'All', isActive: true, createdAt: '2026-01-20',
  },
];

export const MOCK_FEED_STOCK: FeedStock[] = [
  {
    id: 'fs-001', feedTypeId: 'ft-001', feedTypeName: 'Broiler Starter Crumbles', feedCode: 'BRS-001',
    category: 'Starter', farm: 'Idudi Poultry Farm', store: 'Feed Store A',
    quantityKg: 2400, reorderLevelKg: 500, maxCapacityKg: 5000,
    lastReceivedDate: '2026-08-28', lastReceivedQty: 2000, unitCost: 2800, totalValue: 6720000,
    status: 'In Stock', batchNumber: 'BT-2026-089', expiryDate: '2026-11-28', supplier: 'Ugachick Feeds Ltd',
  },
  {
    id: 'fs-002', feedTypeId: 'ft-002', feedTypeName: 'Broiler Grower Pellets', feedCode: 'BRG-002',
    category: 'Grower', farm: 'Idudi Poultry Farm', store: 'Feed Store A',
    quantityKg: 380, reorderLevelKg: 400, maxCapacityKg: 5000,
    lastReceivedDate: '2026-08-20', lastReceivedQty: 3000, unitCost: 2600, totalValue: 988000,
    status: 'Low Stock', batchNumber: 'BT-2026-076', expiryDate: '2026-11-20', supplier: 'Ugachick Feeds Ltd',
  },
  {
    id: 'fs-003', feedTypeId: 'ft-003', feedTypeName: 'Broiler Finisher Pellets', feedCode: 'BRF-003',
    category: 'Finisher', farm: 'Idudi Poultry Farm', store: 'Feed Store B',
    quantityKg: 1850, reorderLevelKg: 600, maxCapacityKg: 6000,
    lastReceivedDate: '2026-09-01', lastReceivedQty: 2500, unitCost: 2500, totalValue: 4625000,
    status: 'In Stock', batchNumber: 'BT-2026-092', expiryDate: '2026-12-01', supplier: 'Ugachick Feeds Ltd',
  },
  {
    id: 'fs-004', feedTypeId: 'ft-004', feedTypeName: 'Layer Mash Premium', feedCode: 'LAY-001',
    category: 'Layer', farm: 'Kampala Layer Farm', store: 'Feed Store C',
    quantityKg: 0, reorderLevelKg: 800, maxCapacityKg: 8000,
    lastReceivedDate: '2026-08-10', lastReceivedQty: 4000, unitCost: 2400, totalValue: 0,
    status: 'Out of Stock', batchNumber: 'BT-2026-065', expiryDate: '2026-11-10', supplier: 'Nile Agro Feeds',
  },
  {
    id: 'fs-005', feedTypeId: 'ft-005', feedTypeName: 'Layer Pellets Hi-Cal', feedCode: 'LAY-002',
    category: 'Layer', farm: 'Kampala Layer Farm', store: 'Feed Store C',
    quantityKg: 3200, reorderLevelKg: 1000, maxCapacityKg: 8000,
    lastReceivedDate: '2026-09-02', lastReceivedQty: 4000, unitCost: 2550, totalValue: 8160000,
    status: 'In Stock', batchNumber: 'BT-2026-093', expiryDate: '2026-12-02', supplier: 'Nile Agro Feeds',
  },
  {
    id: 'fs-006', feedTypeId: 'ft-006', feedTypeName: 'Vitamin & Mineral Premix', feedCode: 'SUP-001',
    category: 'Supplement', farm: 'Idudi Poultry Farm', store: 'Feed Store A',
    quantityKg: 45, reorderLevelKg: 20, maxCapacityKg: 200,
    lastReceivedDate: '2026-08-15', lastReceivedQty: 50, unitCost: 18500, totalValue: 832500,
    status: 'In Stock', batchNumber: 'BT-2026-071', expiryDate: '2027-08-15', supplier: 'Agri-Tech Solutions',
  },
];

export const MOCK_FEED_ISSUANCES: FeedIssuance[] = [
  {
    id: 'fi-001', issuanceNumber: 'FI-2026-0891', date: '2026-09-05',
    feedTypeId: 'ft-001', feedTypeName: 'Broiler Starter Crumbles', feedCode: 'BRS-001',
    flockId: 'flock-001', flockName: 'Broiler Batch A12', house: 'House A1', farm: 'Idudi Poultry Farm',
    quantityIssuedKg: 120, quantityReturnedKg: 0, quantityConsumedKg: 120, wastageKg: 3.5,
    issuedBy: 'Okello James', receivedBy: 'Namukasa Ruth', status: 'Issued',
    notes: 'Morning feeding', birdCount: 2000, feedPerBirdGrams: 60,
  },
  {
    id: 'fi-002', issuanceNumber: 'FI-2026-0890', date: '2026-09-04',
    feedTypeId: 'ft-002', feedTypeName: 'Broiler Grower Pellets', feedCode: 'BRG-002',
    flockId: 'flock-002', flockName: 'Broiler Batch B08', house: 'House B2', farm: 'Idudi Poultry Farm',
    quantityIssuedKg: 200, quantityReturnedKg: 15, quantityConsumedKg: 185, wastageKg: 6.2,
    issuedBy: 'Okello James', receivedBy: 'Ssemakula Peter', status: 'Returned',
    notes: 'Evening feeding — partial return due to feeder malfunction', birdCount: 1800, feedPerBirdGrams: 103,
  },
  {
    id: 'fi-003', issuanceNumber: 'FI-2026-0889', date: '2026-09-04',
    feedTypeId: 'ft-003', feedTypeName: 'Broiler Finisher Pellets', feedCode: 'BRF-003',
    flockId: 'flock-003', flockName: 'Broiler Batch C05', house: 'House C1', farm: 'Idudi Poultry Farm',
    quantityIssuedKg: 350, quantityReturnedKg: 0, quantityConsumedKg: 350, wastageKg: 8.0,
    issuedBy: 'Namukasa Ruth', receivedBy: 'Kato David', status: 'Issued',
    notes: 'Pre-market feeding — day 38', birdCount: 1500, feedPerBirdGrams: 233,
  },
  {
    id: 'fi-004', issuanceNumber: 'FI-2026-0888', date: '2026-09-03',
    feedTypeId: 'ft-005', feedTypeName: 'Layer Pellets Hi-Cal', feedCode: 'LAY-002',
    flockId: 'flock-004', flockName: 'Layer Flock L03', house: 'House D1', farm: 'Kampala Layer Farm',
    quantityIssuedKg: 180, quantityReturnedKg: 0, quantityConsumedKg: 180, wastageKg: 2.1,
    issuedBy: 'Ssemakula Peter', receivedBy: 'Apio Grace', status: 'Issued',
    notes: 'Standard daily ration', birdCount: 3000, feedPerBirdGrams: 60,
  },
  {
    id: 'fi-005', issuanceNumber: 'FI-2026-0887', date: '2026-09-03',
    feedTypeId: 'ft-001', feedTypeName: 'Broiler Starter Crumbles', feedCode: 'BRS-001',
    flockId: 'flock-005', flockName: 'Broiler Batch D14', house: 'House A2', farm: 'Idudi Poultry Farm',
    quantityIssuedKg: 80, quantityReturnedKg: 0, quantityConsumedKg: 80, wastageKg: 1.8,
    issuedBy: 'Kato David', receivedBy: 'Namukasa Ruth', status: 'Issued',
    notes: 'Day 5 chicks', birdCount: 2500, feedPerBirdGrams: 32,
  },
  {
    id: 'fi-006', issuanceNumber: 'FI-2026-0886', date: '2026-09-02',
    feedTypeId: 'ft-002', feedTypeName: 'Broiler Grower Pellets', feedCode: 'BRG-002',
    flockId: 'flock-001', flockName: 'Broiler Batch A12', house: 'House A1', farm: 'Idudi Poultry Farm',
    quantityIssuedKg: 160, quantityReturnedKg: 0, quantityConsumedKg: 160, wastageKg: 4.0,
    issuedBy: 'Okello James', receivedBy: 'Namukasa Ruth', status: 'Issued',
    notes: '', birdCount: 2000, feedPerBirdGrams: 80,
  },
];

export const MOCK_FEED_CONSUMPTION: FeedConsumption[] = [
  {
    id: 'fc-001', date: '2026-09-05', flockId: 'flock-001', flockName: 'Broiler Batch A12',
    house: 'House A1', farm: 'Idudi Poultry Farm', feedTypeName: 'Broiler Starter Crumbles', feedCode: 'BRS-001',
    openingStockKg: 50, issuedKg: 120, returnedKg: 0, closingStockKg: 46.5, consumedKg: 123.5, wastageKg: 3.5,
    birdCount: 2000, avgConsumptionPerBirdGrams: 61.75, weekNumber: 2,
  },
  {
    id: 'fc-002', date: '2026-09-04', flockId: 'flock-002', flockName: 'Broiler Batch B08',
    house: 'House B2', farm: 'Idudi Poultry Farm', feedTypeName: 'Broiler Grower Pellets', feedCode: 'BRG-002',
    openingStockKg: 80, issuedKg: 200, returnedKg: 15, closingStockKg: 88.8, consumedKg: 191.2, wastageKg: 6.2,
    birdCount: 1800, avgConsumptionPerBirdGrams: 106.2, weekNumber: 3,
  },
  {
    id: 'fc-003', date: '2026-09-04', flockId: 'flock-003', flockName: 'Broiler Batch C05',
    house: 'House C1', farm: 'Idudi Poultry Farm', feedTypeName: 'Broiler Finisher Pellets', feedCode: 'BRF-003',
    openingStockKg: 120, issuedKg: 350, returnedKg: 0, closingStockKg: 112, consumedKg: 358, wastageKg: 8.0,
    birdCount: 1500, avgConsumptionPerBirdGrams: 238.7, weekNumber: 5,
  },
  {
    id: 'fc-004', date: '2026-09-03', flockId: 'flock-004', flockName: 'Layer Flock L03',
    house: 'House D1', farm: 'Kampala Layer Farm', feedTypeName: 'Layer Pellets Hi-Cal', feedCode: 'LAY-002',
    openingStockKg: 30, issuedKg: 180, returnedKg: 0, closingStockKg: 27.9, consumedKg: 182.1, wastageKg: 2.1,
    birdCount: 3000, avgConsumptionPerBirdGrams: 60.7, weekNumber: 22,
  },
  {
    id: 'fc-005', date: '2026-09-03', flockId: 'flock-005', flockName: 'Broiler Batch D14',
    house: 'House A2', farm: 'Idudi Poultry Farm', feedTypeName: 'Broiler Starter Crumbles', feedCode: 'BRS-001',
    openingStockKg: 20, issuedKg: 80, returnedKg: 0, closingStockKg: 18.2, consumedKg: 81.8, wastageKg: 1.8,
    birdCount: 2500, avgConsumptionPerBirdGrams: 32.7, weekNumber: 1,
  },
];

export const MOCK_FEED_RETURNS: FeedReturn[] = [
  {
    id: 'fr-001', returnNumber: 'FR-2026-0045', date: '2026-09-04',
    issuanceId: 'fi-002', issuanceNumber: 'FI-2026-0890',
    feedTypeName: 'Broiler Grower Pellets', flockName: 'Broiler Batch B08',
    house: 'House B2', farm: 'Idudi Poultry Farm',
    returnedQtyKg: 15, reason: 'Feeder malfunction — excess feed recovered',
    condition: 'Good', returnedBy: 'Ssemakula Peter', receivedBy: 'Okello James',
    notes: 'Feed returned in good condition, stored back in Feed Store A',
  },
  {
    id: 'fr-002', returnNumber: 'FR-2026-0044', date: '2026-09-01',
    issuanceId: 'fi-003', issuanceNumber: 'FI-2026-0882',
    feedTypeName: 'Broiler Finisher Pellets', flockName: 'Broiler Batch C05',
    house: 'House C1', farm: 'Idudi Poultry Farm',
    returnedQtyKg: 22, reason: 'Over-issued — flock count adjusted downward',
    condition: 'Good', returnedBy: 'Kato David', receivedBy: 'Namukasa Ruth',
    notes: 'Returned same day, no quality issues',
  },
  {
    id: 'fr-003', returnNumber: 'FR-2026-0043', date: '2026-08-30',
    issuanceId: 'fi-004', issuanceNumber: 'FI-2026-0875',
    feedTypeName: 'Layer Mash Premium', flockName: 'Layer Flock L02',
    house: 'House D2', farm: 'Kampala Layer Farm',
    returnedQtyKg: 8, reason: 'Contamination suspected — wet feed near water line',
    condition: 'Contaminated', returnedBy: 'Apio Grace', receivedBy: 'Ssemakula Peter',
    notes: 'Quarantined for disposal — do not reuse',
  },
];

export const MOCK_FCR_RECORDS: FCRRecord[] = [
  {
    id: 'fcr-001', flockId: 'flock-001', flockName: 'Broiler Batch A12',
    house: 'House A1', farm: 'Idudi Poultry Farm', birdType: 'Broiler',
    weekNumber: 2, periodStart: '2026-08-25', periodEnd: '2026-09-01',
    totalFeedConsumedKg: 840, totalWeightGainKg: 560, fcr: 1.50, targetFcr: 1.55,
    fcrVariance: -0.05, avgBodyWeightKg: 0.42, feedCostPerKg: 2800, totalFeedCost: 2352000,
    status: 'Excellent',
  },
  {
    id: 'fcr-002', flockId: 'flock-002', flockName: 'Broiler Batch B08',
    house: 'House B2', farm: 'Idudi Poultry Farm', birdType: 'Broiler',
    weekNumber: 3, periodStart: '2026-08-25', periodEnd: '2026-09-01',
    totalFeedConsumedKg: 1260, totalWeightGainKg: 720, fcr: 1.75, targetFcr: 1.70,
    fcrVariance: 0.05, avgBodyWeightKg: 0.82, feedCostPerKg: 2600, totalFeedCost: 3276000,
    status: 'Good',
  },
  {
    id: 'fcr-003', flockId: 'flock-003', flockName: 'Broiler Batch C05',
    house: 'House C1', farm: 'Idudi Poultry Farm', birdType: 'Broiler',
    weekNumber: 5, periodStart: '2026-08-25', periodEnd: '2026-09-01',
    totalFeedConsumedKg: 2450, totalWeightGainKg: 1050, fcr: 2.33, targetFcr: 2.10,
    fcrVariance: 0.23, avgBodyWeightKg: 2.10, feedCostPerKg: 2500, totalFeedCost: 6125000,
    status: 'Average',
  },
  {
    id: 'fcr-004', flockId: 'flock-004', flockName: 'Layer Flock L03',
    house: 'House D1', farm: 'Kampala Layer Farm', birdType: 'Layer',
    weekNumber: 22, periodStart: '2026-08-25', periodEnd: '2026-09-01',
    totalFeedConsumedKg: 1260, totalWeightGainKg: 0, fcr: 0, targetFcr: 0,
    fcrVariance: 0, avgBodyWeightKg: 1.85, feedCostPerKg: 2550, totalFeedCost: 3213000,
    status: 'Good',
  },
  {
    id: 'fcr-005', flockId: 'flock-005', flockName: 'Broiler Batch D14',
    house: 'House A2', farm: 'Idudi Poultry Farm', birdType: 'Broiler',
    weekNumber: 1, periodStart: '2026-08-25', periodEnd: '2026-09-01',
    totalFeedConsumedKg: 350, totalWeightGainKg: 175, fcr: 2.00, targetFcr: 1.80,
    fcrVariance: 0.20, avgBodyWeightKg: 0.18, feedCostPerKg: 2800, totalFeedCost: 980000,
    status: 'Average',
  },
];

export const FEED_SUMMARY = {
  totalFeedTypes: MOCK_FEED_TYPES.length,
  activeFeedTypes: MOCK_FEED_TYPES.filter(f => f.isActive).length,
  totalStockValueUGX: MOCK_FEED_STOCK.reduce((s, f) => s + f.totalValue, 0),
  lowStockItems: MOCK_FEED_STOCK.filter(f => f.status === 'Low Stock').length,
  outOfStockItems: MOCK_FEED_STOCK.filter(f => f.status === 'Out of Stock').length,
  todayIssuancesKg: MOCK_FEED_ISSUANCES.filter(f => f.date === '2026-09-05').reduce((s, f) => s + f.quantityIssuedKg, 0),
  totalWastageKg: MOCK_FEED_ISSUANCES.reduce((s, f) => s + f.wastageKg, 0),
  avgFCR: parseFloat((MOCK_FCR_RECORDS.filter(f => f.fcr > 0).reduce((s, f) => s + f.fcr, 0) / MOCK_FCR_RECORDS.filter(f => f.fcr > 0).length).toFixed(2)),
};
